import {
  captureReactException,
  setContext,
  setExtras,
  setTags,
  setUser,
} from '@sentry/react';

import { OneErrorContext } from '#/hooks/useDataContext';
import { AppStatus } from '#/service/constants';

import { isOneError, OneError, OneErrorDetails } from './oneError';
import {
  ApiError,
  AuthorizationError,
  GeoRestrictionError,
  PlaybackError,
  UserAccessError,
} from './serviceErrors';

// these are considered "expected" errors and should not be logged to Sentry
const IGNORED_STATUS_CODES = new Set([401, 403, 410]);
// for some errors, error code is not relevant, but let's be explicit about it
const NO_CODE = 'NO_CODE';

export const logErrorToSentry = (
  error: Error | OneError | undefined,
  context: OneErrorContext,
  message: string,
  componentStack?: string
) => {
  const {
    code,
    timestamp,
    errorSpecVersion,
    variationHash,
    deviceId,
    userId,
    clientIp,
    clientVersion,
    requestIds,
    cdnRequestIds,
    relatedErrorCodes,
    app,
    extras,
  } = context;

  // intentionally no fallback to 'anonymous', because we don't want to pretend that ALL anonymous users are ONE user.
  setUser({ id: userId });

  // all non-singular context keys should be logged as separate contexts in Sentry.
  setContext('requestIds', {
    value: requestIds?.join(','),
  });
  setContext('cdnRequestIds', {
    value: cdnRequestIds?.join(','),
  });
  setContext('relatedErrorCodes', {
    value: relatedErrorCodes?.join(','),
  });

  // all singular context keys above should be logged as tags in Sentry.
  setTags({
    code,
    timestamp,
    errorSpecVersion,
    applicationId: app.id,
    variationHash,
    deviceId,
    // fallback to 'anonymous' just so we can distinguish between missing and anonymous.
    userId: userId || 'anonymous',
    clientIp: clientIp || 'unknown',
    clientVersion,
    applicationVersion: app.version,
  });

  // all extra parameters should be logged as extras in Sentry.
  setExtras(extras);

  // if we have a oneError we use the provided message, otherwise we use the translated message.
  const sentryError = error ?? new Error(message);
  sentryError.name = code;

  captureReactException(sentryError, { componentStack });
};

// Check if we want to send the log for the specific error case and return the connected values.
export const filterSentryLogs = (
  error: OneError | Error | undefined,
  status: AppStatus | undefined
): { sendSentryLogs: boolean; code: string; details?: OneErrorDetails } => {
  // if the status IS the error, we send Sentry logs.
  if (!error && status && appStatusToTrackInSentry.includes(status)) {
    return { sendSentryLogs: true, code: status };
  }

  // if the error is a OneError, we send Sentry logs, unless the error is excluded via `preventSentry`
  if (isOneError(error)) {
    return {
      sendSentryLogs: !error.details?.preventSentry,
      code: error.code,
      details: error.details,
    };
  }

  // if the error is an ApiError, we ignore "expected" status codes.
  if (error instanceof ApiError) {
    const ignored = IGNORED_STATUS_CODES.has(error.statusCode);

    return {
      sendSentryLogs: !ignored,
      code: String(error.statusCode),
      details: error.details,
    };
  }

  // if the error is an "expected" custom service error, we ignore it.
  if (
    error instanceof GeoRestrictionError ||
    error instanceof UserAccessError ||
    error instanceof AuthorizationError
  ) {
    return { sendSentryLogs: false, code: NO_CODE };
  }

  // if the error is an "unexpected" custom service error, we send Sentry logs.
  if (error instanceof PlaybackError) {
    return { sendSentryLogs: true, code: error.message };
  }

  // if the error is an Error, we send Sentry logs.
  if (error instanceof Error) {
    return { sendSentryLogs: true, code: error.name };
  }

  // for everything else, we ignore it.
  return { sendSentryLogs: false, code: NO_CODE };
};

// List of app statuses that we want to track in Sentry
export const appStatusToTrackInSentry: AppStatus[] = [
  AppStatus.Error,
  AppStatus.Playback,
  AppStatus.Maintenance,
  AppStatus.Configuration,
];
